import { computed, watch } from 'vue';
import { SelectionPrefix } from '@sembo-private/constraint-builder';
import type { Observable } from 'rxjs';
import { catchError, map } from 'rxjs';

import type {
  PortStaticInfo,
  Dictionary,
  TransportVehicle,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import {
  AutoCompleteSearchType,
  CategoryType,
  IngredientType,
  TransportVehicleType,
} from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';

import type { RecipeState } from '../state/recipeState';
import type { TripState } from '../state/tripState';
import type TripPlannerModel from '../tripPlannerModel/TripPlannerModel';
import type { HotelIngredient } from '../recipe/ingredients';
import type { OwnStayIngredient } from '../recipe/ingredients/ownStayIngredient';
import { isHotelIngredient } from '../recipe/ingredients/typeGuards';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useRecipeViewModel(
  recipeState: RecipeState,
  tripState: TripState,
  model: TripPlannerModel
) {
  const recipeIatas = computed(() =>
    recipeState.flightOnlyRecipe.value.flatMap((flight) => {
      assertValue(flight.fromPort, 'RecipeViewModel > recipeIatas > fromPort');
      assertValue(flight.toPort, 'RecipeViewModel > recipeIatas > toPort');
      return [flight.fromPort, flight.toPort];
    })
  );
  watch(
    recipeIatas,
    (iatas) => {
      tripState.isSearchingFlightsStaticInfo.value = true;

      model.searchAirportStaticInfo(iatas).subscribe({
        next: (ports: Dictionary<PortStaticInfo>) => {
          tripState.insertPortStaticInfo(ports, IngredientType.Flight);
          tripState.setFlightStaticInfoToRecipe();
        },
        complete: () => {
          tripState.isSearchingFlightsStaticInfo.value = false;
        },
      });
    },
    { immediate: true }
  );

  const recipeSeaports = computed(() =>
    recipeState.ferryOnlyRecipe.value.flatMap((ferry) => {
      assertValue(
        ferry.fromPort,
        'RecipeViewModel > recipeSeaports > fromPort'
      );
      assertValue(ferry.toPort, 'RecipeViewModel > recipeSeaports > toPort');
      return [ferry.fromPort, ferry.toPort];
    })
  );
  watch(
    recipeSeaports,
    (seaports) => {
      tripState.isSearchingFerriesStaticInfo.value = true;

      model.searchSeaportStaticInfo(seaports).subscribe({
        next: (ports: Dictionary<PortStaticInfo>) => {
          tripState.insertPortStaticInfo(ports, IngredientType.Ferry);
          tripState.setFerryStaticInfoToRecipe();
        },
        complete: () => {
          tripState.isSearchingFerriesStaticInfo.value = false;
        },
        error: () => (tripState.isSearchingFerriesStaticInfo.value = false),
      });
    },
    { immediate: true }
  );

  model.getFerryVehicleTypes().subscribe(
    (response) =>
      (tripState.ferryVehicleTypes.value = response.reduce(
        (acc: TransportVehicle[], vehicle: TransportVehicle) => {
          if (vehicle.id === TransportVehicleType.None) {
            acc.unshift(vehicle);
          } else {
            acc.push(vehicle);
          }

          return acc;
        },
        []
      ))
  );

  const recipeStaySearchCriterias = computed(() =>
    recipeState.stayOnlyRecipe.value.map((stayIngredient) => {
      const { searchText, ingredientId } = stayIngredient;

      return {
        searchText,
        ingredientId,
        polygonId: isHotelIngredient(stayIngredient)
          ? stayIngredient.polygonId
          : undefined,
      };
    })
  );
  watch(
    recipeStaySearchCriterias,
    (newStaySearchCriterias, oldStaySearchCriterias) => {
      newStaySearchCriterias.forEach(
        ({ searchText, polygonId, ingredientId }) => {
          const stayIngredient = recipeState.getStayIngredient(ingredientId);

          if (!stayIngredient) {
            return;
          }

          const oldSearchCriteria = oldStaySearchCriterias?.find(
            (oldSearchCriteria) =>
              oldSearchCriteria.ingredientId === ingredientId
          );

          const hasChangedSearchText =
            searchText !== oldSearchCriteria?.searchText;
          const hasChangedPolygonId =
            polygonId !== oldSearchCriteria?.polygonId;

          if (hasChangedSearchText) {
            updateStayPosition(stayIngredient);
            updateDisplayText(stayIngredient);
            if (isHotelIngredient(stayIngredient)) {
              updatePolygonId(stayIngredient);
              updateNearbyPolygon(stayIngredient);
            }
          }

          if (hasChangedPolygonId && polygonId) {
            updateDestinationInformation(polygonId, ingredientId);
          }
        }
      );
    },
    { immediate: true }
  );

  function updateStayPosition(
    ingredient: HotelIngredient | OwnStayIngredient
  ): void {
    model
      .searchHotelPosition(ingredient.searchText)
      .subscribe(
        (hotelPositionResult) =>
          (ingredient.positionFromSearchText = hotelPositionResult)
      );
  }

  function updateDisplayText(
    stayIngredient: HotelIngredient | OwnStayIngredient
  ): void {
    const SELECTION_PREFIXES = Object.values(SelectionPrefix).filter(
      (prefix) => prefix !== SelectionPrefix.FullText
    );

    const hasSerializedConstraintSearchText = SELECTION_PREFIXES.some(
      (prefix) => stayIngredient.searchText.startsWith(prefix)
    );

    if (
      hasSerializedConstraintSearchText &&
      (!stayIngredient.displayText ||
        stayIngredient.displayText === stayIngredient.searchText)
    ) {
      if (stayIngredient.searchText.startsWith(SelectionPrefix.HotelCode)) {
        model
          .searchHotelName(stayIngredient.searchText)
          .subscribe((response) => {
            stayIngredient.displayText = response;
          });
      } else {
        model
          .searchHotelMainPolygonName(stayIngredient.searchText)
          .subscribe((response) => {
            stayIngredient.displayText = response;
          });
      }
    }
  }

  function updatePolygonId(hotelIngredient: HotelIngredient): void {
    if (hotelIngredient.polygonId) {
      return;
    }

    model
      .searchAutoCompleteSuggestions({
        searchType: AutoCompleteSearchType.Destination,
        searchText: hotelIngredient.searchText,
      })
      .subscribe((suggestionCategories) => {
        const suggestedPolygon = suggestionCategories.filter(
          (suggestionCategory) =>
            suggestionCategory.type === CategoryType.PolygonCityZone
        )[0]?.data[0];

        recipeState.shouldSkipItinerarySearch.value = true;

        hotelIngredient.polygonId =
          !!suggestedPolygon && 'polygonId' in suggestedPolygon
            ? suggestedPolygon.polygonId
            : undefined;
      });
  }

  function updateNearbyPolygon(hotelIngredient: HotelIngredient): void {
    if (hotelIngredient.searchText.startsWith(SelectionPrefix.HotelCode)) {
      model
        .searchHotelMainPolygonName(hotelIngredient.searchText)
        .subscribe(
          (response) => (hotelIngredient.nearbyPolygonText = response)
        );
      return;
    }

    checkSearchTextIsHotelName(hotelIngredient.searchText).subscribe(
      (isHotelNameSearchText) => {
        if (isHotelNameSearchText) {
          model
            .searchHotelMainPolygonName(hotelIngredient.searchText)
            .subscribe(
              (response) => (hotelIngredient.nearbyPolygonText = response)
            );
        }
      }
    );
  }

  function checkSearchTextIsHotelName(searchText: string): Observable<boolean> {
    return model
      .searchAutoCompleteSuggestions({
        searchText,
        searchType: AutoCompleteSearchType.Destination,
      })
      .pipe(
        map((suggestionResult) => {
          if (
            !!suggestionResult[0] &&
            suggestionResult[0].type === CategoryType.Hotel
          ) {
            const isHotelNameSearchText =
              suggestionResult[0].type === CategoryType.Hotel;

            return isHotelNameSearchText;
          } else {
            return false;
          }
        })
      );
  }

  function updateDestinationInformation(
    polygonId: number,
    ingredientId: IngredientId
  ): void {
    model
      .searchDestinationInformation(polygonId)
      .pipe(
        catchError((error) => {
          throw new Error(
            `Failed to fetch destination information: ${error.message || error}`
          );
        })
      )
      .subscribe((destinationInformation) => {
        tripState.addDestinationInformation(
          destinationInformation,
          ingredientId
        );
      });
  }
}
